"use client";
import { useMemo } from "react";
import type { EnrichedProgram, ProcessedCourse } from "@/common/course/types";
import type {
  RelatedProductsFilter,
  RelatedProductsSource,
} from "@/monarch/types/ProductCompare";
import {
  programMatchesCompareFilters,
  resolveRelatedProductsFilters,
} from "./buildCompareFacetFilters";
import useRelatedCourses from "./useRelatedCourses";

export interface UseCompareFallbackUuidsOptions {
  source?: RelatedProductsSource;
  enrichedPrograms?: EnrichedProgram[] | null;
  course?: ProcessedCourse;
  relatedProductsFilters?: RelatedProductsFilter[];
}

const extractProgramUuids = (
  enrichedPrograms: EnrichedProgram[] | null | undefined,
  course: ProcessedCourse | undefined,
  enabledFilters: RelatedProductsFilter[],
): string[] => {
  if (!course) return [];

  return (enrichedPrograms ?? [])
    .filter((program) =>
      programMatchesCompareFilters(program, course, enabledFilters),
    )
    .map((program) => program.uuid)
    .filter((uuid): uuid is string => Boolean(uuid));
};

export function useCompareFallbackUuids({
  source = "programs",
  enrichedPrograms,
  course,
  relatedProductsFilters,
}: UseCompareFallbackUuidsOptions): string[] {
  const wantsCourses = source === "courses";
  const enabledFilters = resolveRelatedProductsFilters(
    relatedProductsFilters,
    "course",
    source,
  );
  const { uuids: relatedCourseUuids } = useRelatedCourses({
    course,
    enabled: wantsCourses,
    relatedProductsFilters: enabledFilters,
  });

  return useMemo(() => {
    if (wantsCourses) return relatedCourseUuids;
    return extractProgramUuids(enrichedPrograms, course, enabledFilters);
  }, [
    wantsCourses,
    relatedCourseUuids,
    enrichedPrograms,
    course,
    enabledFilters.join("|"),
  ]);
}

export default useCompareFallbackUuids;
